import { useEffect, useRef, useState, useCallback } from 'react';
import { FiDownload, FiZoomIn, FiZoomOut, FiMaximize, FiMove, FiEdit2 } from 'react-icons/fi';
import { useDiagram } from '../../contexts/DiagramContext';
import useMermaid from '../../hooks/useMermaid';
import ElementEditor from '../ElementEditor/ElementEditor';
import './DiagramRenderer.css';

const DiagramRenderer = () => {
  const { code, updateCode } = useDiagram();
  const { svg, error, loading, render, exportSVG, exportPNG } = useMermaid(code);
  const rendererRef = useRef(null);
  const svgContainerRef = useRef(null);
  
  // State for zoom and pan
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPanPosition, setStartPanPosition] = useState({ x: 0, y: 0 });
  
  // State for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  
  // State for element editing
  const [elementEditor, setElementEditor] = useState({
    visible: false,
    position: { x: 0, y: 0 },
    element: null,
    nodeId: null,
    nodeType: null,
  });

  // Render the diagram when the component mounts
  useEffect(() => {
    render();
  }, [render]);

  // Center the diagram when SVG changes
  useEffect(() => {
    if (svg && rendererRef.current && svgContainerRef.current) {
      // Reset position to center when a new diagram is rendered
      const containerRect = rendererRef.current.getBoundingClientRect();
      const svgRect = svgContainerRef.current.getBoundingClientRect();
      
      // Calculate the center position
      const centerX = (containerRect.width - svgRect.width) / 2;
      const centerY = (containerRect.height - svgRect.height) / 2;
      
      // Only center if the position is at the default (0,0)
      if (position.x === 0 && position.y === 0) {
        setPosition({ x: centerX, y: centerY });
      }
    }
  }, [svg]);

  // Handle manual render button click
  const handleRender = () => {
    render();
    // Reset zoom and position when rendering a new diagram
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    // Exit edit mode
    setIsEditMode(false);
  };

  // Handle zoom in
  const handleZoomIn = () => {
    setZoom(prevZoom => Math.min(prevZoom + 0.1, 3));
  };

  // Handle zoom out
  const handleZoomOut = () => {
    setZoom(prevZoom => Math.max(prevZoom - 0.1, 0.5));
  };

  // Handle reset zoom and position
  const handleResetView = () => {
    setZoom(1);
    
    // Center the diagram if possible
    if (rendererRef.current && svgContainerRef.current) {
      const containerRect = rendererRef.current.getBoundingClientRect();
      const svgRect = svgContainerRef.current.getBoundingClientRect();
      
      // Calculate the center position
      const centerX = (containerRect.width - svgRect.width) / 2;
      const centerY = (containerRect.height - svgRect.height) / 2;
      
      setPosition({ x: centerX, y: centerY });
    } else {
      // Fallback to default position
      setPosition({ x: 0, y: 0 });
    }
  };

  // Handle toggle panning mode
  const handleTogglePanning = () => {
    setIsPanning(!isPanning);
    // Exit edit mode when entering pan mode
    if (!isPanning) {
      setIsEditMode(false);
    }
  };

  // Handle toggle edit mode
  const handleToggleEditMode = () => {
    setIsEditMode(!isEditMode);
    // Exit pan mode when entering edit mode
    if (!isEditMode) {
      setIsPanning(false);
    } else {
      // Hide element editor when exiting edit mode
      setElementEditor({ ...elementEditor, visible: false });
    }
  };

  // Handle mouse down for panning
  const handleMouseDown = (e) => {
    if (!isPanning || !svg || !rendererRef.current) return;
    
    // Get the bounding rectangle of the container
    const rect = rendererRef.current.getBoundingClientRect();
    
    // Calculate coordinates relative to the container
    const relativeX = e.clientX - rect.left;
    const relativeY = e.clientY - rect.top;
    
    setStartPanPosition({
      x: relativeX - position.x,
      y: relativeY - position.y
    });
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle mouse move for panning
  const handleMouseMove = (e) => {
    if (!rendererRef.current) return;
    
    // Get the bounding rectangle of the container
    const rect = rendererRef.current.getBoundingClientRect();
    
    // Calculate coordinates relative to the container
    const relativeX = e.clientX - rect.left;
    const relativeY = e.clientY - rect.top;
    
    setPosition({
      x: relativeX - startPanPosition.x,
      y: relativeY - startPanPosition.y
    });
  };

  // Handle mouse up for panning
  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Handle wheel event for zooming
  const handleWheel = (e) => {
    if (!svg) return;
    
    e.preventDefault();
    
    // Determine zoom direction
    const zoomFactor = e.deltaY < 0 ? 0.1 : -0.1;
    const newZoom = Math.max(0.5, Math.min(3, zoom + zoomFactor));
    
    setZoom(newZoom);
  };

  // Function to handle node element click
  const handleNodeClick = (e, nodeElement) => {
    if (!isEditMode || !nodeElement) return;
    
    e.stopPropagation();
    
    console.log('Node clicked:', nodeElement);
    
    // Get the node ID
    let nodeId = nodeElement.id;
    
    // If no direct ID, try to find it from parent elements
    if (!nodeId) {
      let parent = nodeElement.parentElement;
      while (parent && !nodeId) {
        if (parent.id) {
          nodeId = parent.id;
        }
        parent = parent.parentElement;
      }
    }
    
    console.log('Node ID:', nodeId);
    
    // Determine node type
    let nodeType = 'node';
    if (nodeElement.classList && nodeElement.classList.contains('edge')) {
      nodeType = 'edge';
    } else if (nodeElement.classList && nodeElement.classList.contains('cluster')) {
      nodeType = 'cluster';
    }
    
    // Extract the clean node ID from the full ID
    let cleanNodeId = '';
    if (nodeId) {
      // Remove common prefixes
      cleanNodeId = nodeId.replace('flowchart-', '')
                          .replace('classid-', '')
                          .replace('state-', '')
                          .replace('sequenceDiagram-', '');
      
      // For flowchart nodes, the ID might have additional parts
      if (nodeId.startsWith('flowchart-')) {
        const parts = cleanNodeId.split('-');
        if (parts.length > 0) {
          cleanNodeId = parts[0]; // Use the first part as the node ID
        }
      }
    }
    
    console.log('Clean node ID:', cleanNodeId);
    
    // Get the node label
    let label = '';
    
    // First try to get the label from text elements
    const textElement = nodeElement.querySelector('text');
    if (textElement && textElement.textContent.trim()) {
      label = textElement.textContent.trim();
    } 
    // If no text element or it's empty, try to get the label from the node definition
    else if (cleanNodeId) {
      // Try to find the node definition in the code with square brackets []
      const squareBracketPattern = new RegExp(`\\b${cleanNodeId}\\s*\\[([^\\]]*)\\]`, 'g');
      const squareMatch = squareBracketPattern.exec(code);
      
      if (squareMatch && squareMatch[1]) {
        label = squareMatch[1].trim();
      } else {
        // Try to find the node definition with curly braces {}
        const curlyBracePattern = new RegExp(`\\b${cleanNodeId}\\s*\\{([^}]*)\\}`, 'g');
        const curlyMatch = curlyBracePattern.exec(code);
        
        if (curlyMatch && curlyMatch[1]) {
          label = curlyMatch[1].trim();
        } else {
          // If still no label, use the node ID as the label
          label = cleanNodeId;
        }
      }
    }
    
    console.log('Node label:', label);
    
    // Try to determine current styles from the SVG
    let fillColor = '#ffffff';
    let textColor = '#000000';
    let borderColor = '#000000';
    let borderStyle = 'solid';
    
    // Check for fill color in the SVG
    const rectElement = nodeElement.querySelector('rect, circle, ellipse, polygon');
    if (rectElement) {
      const fill = rectElement.getAttribute('fill');
      if (fill && fill !== 'none') {
        fillColor = fill;
      }
      
      const stroke = rectElement.getAttribute('stroke');
      if (stroke) {
        borderColor = stroke;
      }
      
      const strokeDasharray = rectElement.getAttribute('stroke-dasharray');
      if (strokeDasharray) {
        borderStyle = 'dashed';
      }
    }
    
    // Check for text color in the SVG
    if (textElement) {
      const fill = textElement.getAttribute('fill');
      if (fill) {
        textColor = fill;
      }
    }
    
    // Now try to extract style information from the Mermaid code
    if (cleanNodeId) {
      // For nodes, look for style statements
      if (nodeType === 'node') {
        const stylePattern = new RegExp(`style\\s+${cleanNodeId}\\s+([^\\n]+)`, 'g');
        const styleMatch = stylePattern.exec(code);
        
        if (styleMatch && styleMatch[1]) {
          // Parse the style properties
          const styleProps = styleMatch[1].split(',');
          styleProps.forEach(prop => {
            const [key, value] = prop.split(':').map(s => s.trim());
            if (key && value) {
              // Update the style properties
              if (key === 'fill') {
                fillColor = value;
              } else if (key === 'color') {
                textColor = value;
              } else if (key === 'stroke') {
                borderColor = value;
              } else if (key === 'stroke-dasharray') {
                borderStyle = 'dashed';
              }
            }
          });
        }
      } 
      // For edges, look for linkStyle statements
      else if (nodeType === 'edge') {
        // Find the edge index
        const edgePattern = /(\w+)\s*-->\s*(\w+)/g;
        const edges = [...code.matchAll(edgePattern)];
        let edgeIndex = -1;
        
        for (let i = 0; i < edges.length; i++) {
          const edge = edges[i];
          const edgeText = edge[0];
          
          if (edgeText.includes(cleanNodeId)) {
            edgeIndex = i;
            break;
          }
        }
        
        if (edgeIndex !== -1) {
          // Look for linkStyle for this edge
          const linkStylePattern = new RegExp(`linkStyle\\s+${edgeIndex}\\s+([^\\n]+)`, 'g');
          const linkStyleMatch = linkStylePattern.exec(code);
          
          if (linkStyleMatch && linkStyleMatch[1]) {
            // Parse the style properties
            const styleProps = linkStyleMatch[1].split(',');
            styleProps.forEach(prop => {
              const [key, value] = prop.split(':').map(s => s.trim());
              if (key && value) {
                // Update the style properties
                if (key === 'color') {
                  textColor = value;
                } else if (key === 'stroke') {
                  borderColor = value;
                } else if (key === 'stroke-dasharray') {
                  borderStyle = 'dashed';
                }
              }
            });
          }
        }
      }
    }
    
    console.log('Extracted styles:', { fillColor, textColor, borderColor, borderStyle });
    
    // Show the element editor
    setElementEditor({
      visible: true,
      element: {
        label,
        fillColor,
        textColor,
        borderColor,
        borderStyle,
      },
      nodeId,
      nodeType,
    });
  };

  // Function to handle text element click
  const handleTextClick = (e, textElement) => {
    if (!textElement) return;
    
    e.stopPropagation();
    
    console.log('Text clicked:', textElement.textContent);
    
    // Get the text content
    let text = textElement.textContent || '';
    text = text.trim();
    console.log('Text content:', text);
    
    // Get the position and dimensions of the text element
    let rect;
    if (textElement.getBoundingClientRect) {
      // Real DOM element
      rect = textElement.getBoundingClientRect();
    } else if (textElement.parentElement && textElement.parentElement.getBoundingClientRect) {
      // Mock element with parent
      rect = textElement.parentElement.getBoundingClientRect();
    } else {
      // Fallback to event coordinates
      rect = {
        left: e.clientX,
        top: e.clientY,
        width: 100,
        height: 20
      };
    }
    
    const containerRect = svgContainerRef.current.getBoundingClientRect();
    
    // Try to find the node ID from the parent element
    let nodeId = null;
    let parentElement = textElement.parentElement;
    
    // First check if the element itself has an ID
    if (textElement.id) {
      nodeId = textElement.id;
    } else if (parentElement) {
      // Then check parent elements
      while (parentElement && !nodeId) {
        if (parentElement.id) {
          nodeId = parentElement.id;
        }
        parentElement = parentElement.parentElement;
      }
      
      // If still no ID, try to find the closest <g> element
      if (!nodeId) {
        const closestG = textElement.closest('g');
        if (closestG && closestG.id) {
          nodeId = closestG.id;
        }
      }
    }
    
    console.log('Node ID:', nodeId);
    
    // Extract the clean node ID from the full ID
    let cleanNodeId = '';
    if (nodeId) {
      // Remove common prefixes
      cleanNodeId = nodeId.replace('flowchart-', '')
                          .replace('classid-', '')
                          .replace('state-', '')
                          .replace('sequenceDiagram-', '');
      
      // For flowchart nodes, the ID might have additional parts
      if (nodeId.startsWith('flowchart-')) {
        const parts = cleanNodeId.split('-');
        if (parts.length > 0) {
          cleanNodeId = parts[0]; // Use the first part as the node ID
        }
      }
    }
    
    console.log('Clean node ID:', cleanNodeId);
    
    // If text is empty but we have a node ID, try to get the label from the node definition
    if (!text && cleanNodeId) {
      // Try to find the node definition in the code with square brackets []
      const squareBracketPattern = new RegExp(`\\b${cleanNodeId}\\s*\\[([^\\]]*)\\]`, 'g');
      const squareMatch = squareBracketPattern.exec(code);
      
      if (squareMatch && squareMatch[1]) {
        text = squareMatch[1].trim();
      } else {
        // Try to find the node definition with curly braces {}
        const curlyBracePattern = new RegExp(`\\b${cleanNodeId}\\s*\\{([^}]*)\\}`, 'g');
        const curlyMatch = curlyBracePattern.exec(code);
        
        if (curlyMatch && curlyMatch[1]) {
          text = curlyMatch[1].trim();
        }
      }
    }
    
    // Determine node type
    let nodeType = 'node';
    if (parentElement) {
      if (parentElement.classList && parentElement.classList.contains('edge')) {
        nodeType = 'edge';
      } else if (parentElement.classList && parentElement.classList.contains('cluster')) {
        nodeType = 'cluster';
      }
    }
    
    // Try to determine current styles from the SVG
    let fillColor = '#ffffff';
    let textColor = '#000000';
    let borderColor = '#000000';
    let borderStyle = 'solid';
    
    // Check for text color in the SVG
    const fill = textElement.getAttribute('fill');
    if (fill) {
      textColor = fill;
    }
    
    // Check for fill and border color in the parent elements
    if (parentElement) {
      const rectElement = parentElement.querySelector('rect, circle, ellipse, polygon');
      if (rectElement) {
        const rectFill = rectElement.getAttribute('fill');
        if (rectFill && rectFill !== 'none') {
          fillColor = rectFill;
        }
        
        const stroke = rectElement.getAttribute('stroke');
        if (stroke) {
          borderColor = stroke;
        }
        
        const strokeDasharray = rectElement.getAttribute('stroke-dasharray');
        if (strokeDasharray) {
          borderStyle = 'dashed';
        }
      }
    }
    
    // Now try to extract style information from the Mermaid code
    if (cleanNodeId) {
      // For nodes, look for style statements
      if (nodeType === 'node') {
        const stylePattern = new RegExp(`style\\s+${cleanNodeId}\\s+([^\\n]+)`, 'g');
        const styleMatch = stylePattern.exec(code);
        
        if (styleMatch && styleMatch[1]) {
          // Parse the style properties
          const styleProps = styleMatch[1].split(',');
          styleProps.forEach(prop => {
            const [key, value] = prop.split(':').map(s => s.trim());
            if (key && value) {
              // Update the style properties
              if (key === 'fill') {
                fillColor = value;
              } else if (key === 'color') {
                textColor = value;
              } else if (key === 'stroke') {
                borderColor = value;
              } else if (key === 'stroke-dasharray') {
                borderStyle = 'dashed';
              }
            }
          });
        }
      } 
      // For edges, look for linkStyle statements
      else if (nodeType === 'edge') {
        // Find the edge index
        const edgePattern = /(\w+)\s*-->\s*(\w+)/g;
        const edges = [...code.matchAll(edgePattern)];
        let edgeIndex = -1;
        
        for (let i = 0; i < edges.length; i++) {
          const edge = edges[i];
          const edgeText = edge[0];
          
          if (edgeText.includes(cleanNodeId)) {
            edgeIndex = i;
            break;
          }
        }
        
        if (edgeIndex !== -1) {
          // Look for linkStyle for this edge
          const linkStylePattern = new RegExp(`linkStyle\\s+${edgeIndex}\\s+([^\\n]+)`, 'g');
          const linkStyleMatch = linkStylePattern.exec(code);
          
          if (linkStyleMatch && linkStyleMatch[1]) {
            // Parse the style properties
            const styleProps = linkStyleMatch[1].split(',');
            styleProps.forEach(prop => {
              const [key, value] = prop.split(':').map(s => s.trim());
              if (key && value) {
                // Update the style properties
                if (key === 'color') {
                  textColor = value;
                } else if (key === 'stroke') {
                  borderColor = value;
                } else if (key === 'stroke-dasharray') {
                  borderStyle = 'dashed';
                }
              }
            });
          }
        }
      }
    }
    
    console.log('Final styles:', { fillColor, textColor, borderColor, borderStyle });
    
    // Show the element editor
    setElementEditor({
      visible: true,
      element: {
        label: text,
        fillColor,
        textColor,
        borderColor,
        borderStyle,
      },
      nodeId,
      nodeType,
    });
  };

  // Add click handlers to text elements in the SVG
  useEffect(() => {
    if (!svg || !svgContainerRef.current) return;
    
    // Use a small delay to ensure the SVG is fully rendered in the DOM
    const timeoutId = setTimeout(() => {
      // Find all text elements in the SVG
      const svgElement = svgContainerRef.current.querySelector('svg');
      if (!svgElement) {
        console.error('SVG element not found');
        return;
      }
      
      // Try different selectors to find text elements
      let textElements = svgElement.querySelectorAll('text');
      console.log('Found text elements with direct selector:', textElements.length);
      
      // If no text elements found, try a more general approach
      if (textElements.length === 0) {
        textElements = svgElement.getElementsByTagName('text');
        console.log('Found text elements with getElementsByTagName:', textElements.length);
      }
      
      // If still no text elements, try to find them in a different way
      if (textElements.length === 0) {
        // Try to find elements that might contain text (like g elements with labels)
        const gElements = svgElement.querySelectorAll('g.label, g.node, g.edgeLabel');
        console.log('Found potential container elements:', gElements.length);
        
        // Check if any of these elements contain text elements
        gElements.forEach(gElement => {
          const textsInG = gElement.querySelectorAll('text');
          if (textsInG.length > 0) {
            console.log('Found text elements in container:', textsInG.length);
          }
        });
      }
      
      // Ensure textElements is iterable before using forEach
      if (textElements && textElements.forEach) {
        // Remove existing click handlers
        textElements.forEach(textElement => {
          const newElement = textElement.cloneNode(true);
          textElement.parentNode.replaceChild(newElement, textElement);
        });
      } else if (textElements) {
        // Convert to array if it's not directly iterable
        Array.from(textElements).forEach(textElement => {
          const newElement = textElement.cloneNode(true);
          textElement.parentNode.replaceChild(newElement, textElement);
        });
      } else {
        console.warn('No text elements found to update');
      }
      
      // Add new click handlers if in edit mode
      if (isEditMode) {
        // Re-query for text elements after replacement
        const updatedTextElements = svgElement.querySelectorAll('text');
        console.log('Adding click handlers to text elements:', updatedTextElements.length);
        
        // Ensure updatedTextElements is iterable
        if (updatedTextElements && updatedTextElements.length > 0) {
          Array.from(updatedTextElements).forEach(textElement => {
            textElement.style.cursor = 'pointer';
            textElement.addEventListener('click', (e) => handleTextClick(e, textElement));
          });
        }
      }
      
      console.log('Finished setting up text element handlers');
    }, 100); // Small delay to ensure SVG is rendered
    
    // Clean up function
    return () => {
      clearTimeout(timeoutId);
      
      const svgElement = svgContainerRef.current?.querySelector('svg');
      if (svgElement) {
        const textElements = svgElement.querySelectorAll('text');
        textElements.forEach(textElement => {
          textElement.style.cursor = '';
        });
        
        const labelElements = svgElement.querySelectorAll('.label');
        labelElements.forEach(labelElement => {
          labelElement.style.cursor = '';
        });
      }
    };
  }, [svg, isEditMode]);

  // Add wheel event listener
  useEffect(() => {
    const container = rendererRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [zoom, svg]);

  // Handle element editor save
  const handleElementEditorSave = (updatedElement) => {
    if (!elementEditor.visible || !elementEditor.nodeId) return;
    
    console.log('Saving element changes:', updatedElement);
    
    // Update the element in the SVG
    updateElementInSvg(updatedElement);
    
    // Update the code
    const updatedCode = updateCodeWithElementChanges(updatedElement);
    updateCode(updatedCode);
    
    // Hide the element editor
    setElementEditor({ ...elementEditor, visible: false });
  };
  
  // Add a useEffect to log when elementEditor changes
  useEffect(() => {
    if (elementEditor.visible) {
      console.log('Element editor opened with:', elementEditor.element);
    }
  }, [elementEditor]);
  
  // Handle element editor delete
  const handleElementEditorDelete = () => {
    if (!elementEditor.visible || !elementEditor.nodeId) return;
    
    console.log('Deleting element:', elementEditor.nodeId);
    
    // Update the code to remove the element
    deleteElementFromCode();
    
    // Hide the element editor
    setElementEditor({ ...elementEditor, visible: false });
    
    // Re-render the diagram
    render();
  };
  
  // Handle element editor create edge
  const handleElementEditorCreateEdge = () => {
    if (!elementEditor.visible || !elementEditor.nodeId) return;
    
    console.log('Creating edge from:', elementEditor.nodeId);
    
    // Start edge creation mode
    // This is a placeholder for now - we'll implement this in a future step
    alert('Edge creation from ' + elementEditor.nodeId + ' is not yet implemented');
    
    // Hide the element editor
    setElementEditor({ ...elementEditor, visible: false });
  };
  
  // Update element in SVG
  const updateElementInSvg = (updatedElement) => {
    if (!svgContainerRef.current || !elementEditor.nodeId) return;
    
    const svgElement = svgContainerRef.current.querySelector('svg');
    if (!svgElement) return;
    
    // Find the node element
    let nodeElement;
    if (elementEditor.nodeType === 'edge') {
      nodeElement = svgElement.querySelector(`#${elementEditor.nodeId}, .edge[id="${elementEditor.nodeId}"]`);
    } else {
      nodeElement = svgElement.querySelector(`#${elementEditor.nodeId}, .node[id="${elementEditor.nodeId}"]`);
    }
    
    if (!nodeElement) {
      console.error('Node element not found:', elementEditor.nodeId);
      return;
    }
    
    // Update the label if it changed
    if (updatedElement.label !== elementEditor.element.label) {
      const textElement = nodeElement.querySelector('text');
      if (textElement) {
        textElement.textContent = updatedElement.label;
      }
    }
    
    // Update the fill color
    const shapeElement = nodeElement.querySelector('rect, circle, ellipse, polygon');
    if (shapeElement) {
      shapeElement.setAttribute('fill', updatedElement.fillColor);
      shapeElement.setAttribute('stroke', updatedElement.borderColor);
      
      // Update border style
      if (updatedElement.borderStyle === 'dashed') {
        shapeElement.setAttribute('stroke-dasharray', '5,5');
      } else {
        shapeElement.removeAttribute('stroke-dasharray');
      }
    }
    
    // Update the text color
    const textElement = nodeElement.querySelector('text');
    if (textElement) {
      textElement.setAttribute('fill', updatedElement.textColor);
    }
  };
  
  // Delete element from code
  const deleteElementFromCode = () => {
    if (!elementEditor.nodeId) return;
    
    try {
      // Extract the node ID without the prefix
      let cleanNodeId = elementEditor.nodeId;
      
      // Remove common prefixes
      ['flowchart-', 'classid-', 'state-', 'sequenceDiagram-'].forEach(prefix => {
        cleanNodeId = cleanNodeId.replace(prefix, '');
      });
      
      // For flowchart nodes, the ID might have additional parts
      if (elementEditor.nodeId.startsWith('flowchart-')) {
        const parts = cleanNodeId.split('-');
        if (parts.length > 0) {
          cleanNodeId = parts[0]; // Use the first part as the node ID
        }
      }
      
      console.log('Deleting element with ID:', cleanNodeId);
      
      let updatedCode = code;
      
      if (elementEditor.nodeType === 'node') {
        // Delete the node definition (handles both square brackets and curly braces)
        const nodePattern = new RegExp(`\\b${cleanNodeId}\\s*(?:\\[[^\\]]*\\]|\\{[^}]*\\})(?:\\s*\\{[^}]*\\})?\\s*`, 'g');
        updatedCode = updatedCode.replace(nodePattern, '');
        
        // Delete any style statements for this node
        const stylePattern = new RegExp(`style\\s+${cleanNodeId}\\s+[^\\n]+\\n?`, 'g');
        updatedCode = updatedCode.replace(stylePattern, '');
        
        // Delete any edges connected to this node
        const edgePattern = new RegExp(`\\b${cleanNodeId}\\s*-->\\s*\\w+(?:\\s*\\|[^|]*\\|)?\\s*\\n?|\\b\\w+\\s*-->\\s*${cleanNodeId}(?:\\s*\\|[^|]*\\|)?\\s*\\n?`, 'g');
        updatedCode = updatedCode.replace(edgePattern, '');
      } else if (elementEditor.nodeType === 'edge') {
        // Find the edge in the code
        const edgePattern = new RegExp(`\\b\\w+\\s*-->\\s*\\w+(?:\\s*\\|([^|]*)\\|)?`, 'g');
        const edges = [...updatedCode.matchAll(edgePattern)];
        
        // Find the edge index
        let edgeIndex = -1;
        let edgeToDelete = null;
        
        for (let i = 0; i < edges.length; i++) {
          const edge = edges[i];
          if (edge[0].includes(cleanNodeId)) {
            edgeIndex = i;
            edgeToDelete = edge[0];
            break;
          }
        }
        
        if (edgeToDelete) {
          // Delete the edge
          updatedCode = updatedCode.replace(edgeToDelete, '');
          
          // Delete any linkStyle statements for this edge
          if (edgeIndex !== -1) {
            const linkStylePattern = new RegExp(`linkStyle\\s+${edgeIndex}\\s+[^\\n]+\\n?`, 'g');
            updatedCode = updatedCode.replace(linkStylePattern, '');
            
            // Update the indices of subsequent linkStyle statements
            const linkStylesPattern = /linkStyle\s+(\d+)\s+/g;
            const linkStyles = [...updatedCode.matchAll(linkStylesPattern)];
            
            for (const linkStyle of linkStyles) {
              const currentIndex = parseInt(linkStyle[1]);
              if (currentIndex > edgeIndex) {
                const newIndex = currentIndex - 1;
                updatedCode = updatedCode.replace(
                  `linkStyle ${currentIndex} `,
                  `linkStyle ${newIndex} `
                );
              }
            }
          }
        }
      }
      
      // Clean up any empty lines
      updatedCode = updatedCode.replace(/\n\s*\n/g, '\n');
      
      // Update the code
      updateCode(updatedCode);
      
      // Close the element editor
      setElementEditor({
        visible: false,
        position: { x: 0, y: 0 },
        element: null,
        nodeId: null,
        nodeType: null,
      });
    } catch (error) {
      console.error('Error deleting element:', error);
    }
  };

  // Update code with element changes
  const updateCodeWithElementChanges = (updatedElement) => {
    if (!elementEditor.nodeId) return code;
    
    // Extract the clean node ID from the full ID
    let cleanNodeId = elementEditor.nodeId.replace('flowchart-', '')
                                      .replace('classid-', '')
                                      .replace('state-', '')
                                      .replace('sequenceDiagram-', '');
    
    // For flowchart nodes, the ID might have additional parts with a dash
    // Example: "C-53" should be treated as just "C"
    if (cleanNodeId.includes('-')) {
      const parts = cleanNodeId.split('-');
      if (parts.length > 0) {
        cleanNodeId = parts[0]; // Use the first part as the node ID
      }
    }
    
    console.log('Using node ID for update:', cleanNodeId);
    
    let updatedCode = code;
    
    // Update the node label if it has changed
    if (updatedElement.label !== elementEditor.element.label) {
      // For nodes, update the label in the node definition
      if (elementEditor.nodeType === 'node') {
        // Check for square bracket syntax first
        const squareBracketPattern = new RegExp(`(\\b${cleanNodeId}\\s*)\\[([^\\]]*)\\]`, 'g');
        const squareMatch = squareBracketPattern.exec(updatedCode);
        
        if (squareMatch) {
          // Update the label in square brackets
          updatedCode = updatedCode.replace(squareBracketPattern, `$1[${updatedElement.label}]`);
          console.log('Updated square bracket node label');
        } else {
          // Check for curly brace syntax
          const curlyBracePattern = new RegExp(`(\\b${cleanNodeId}\\s*)\\{([^}]*)\\}`, 'g');
          const curlyMatch = curlyBracePattern.exec(updatedCode);
          
          if (curlyMatch) {
            // Update the label in curly braces
            updatedCode = updatedCode.replace(curlyBracePattern, `$1{${updatedElement.label}}`);
            console.log('Updated curly brace node label');
          } else {
            // If neither pattern is found, log an error
            console.error(`Could not find node definition for ${cleanNodeId} to update label`);
          }
        }
      }
      // For edges, update the label in the edge definition
      else if (elementEditor.nodeType === 'edge') {
        // Find the edge in the code
        const edgePattern = new RegExp(`(\\w+\\s*-->\\s*\\w+)(\\s*\\|([^|]*)\\|)?`, 'g');
        const edges = [...updatedCode.matchAll(edgePattern)];
        
        // For edges, we need to check if the edge contains our node ID
        // The edge ID might be something like "B-to-C-42", but we need to match "B --> C"
        let edgeFound = false;
        
        for (const edge of edges) {
          const edgeText = edge[0];
          console.log('Checking edge:', edgeText);
          
          // Extract the source and target nodes from the edge
          const edgeMatch = /(\w+)\s*-->\s*(\w+)/.exec(edgeText);
          if (edgeMatch) {
            const sourceNode = edgeMatch[1];
            const targetNode = edgeMatch[2];
            
            // Check if our node is part of this edge
            if (sourceNode === cleanNodeId || targetNode === cleanNodeId) {
              if (edge[2]) {
                // If the edge already has a label, update it
                updatedCode = updatedCode.replace(edge[0], `${edge[1]}|${updatedElement.label}|`);
              } else {
                // If the edge doesn't have a label, add one
                updatedCode = updatedCode.replace(edge[0], `${edge[1]}|${updatedElement.label}|`);
              }
              console.log('Updated edge label');
              edgeFound = true;
              break;
            }
          }
        }
        
        if (!edgeFound) {
          console.error(`Could not find edge containing node ${cleanNodeId} to update label`);
        }
      }
    }
    
    // Update the style properties if they have changed
    const styleChanges = {
      fill: updatedElement.fillColor !== elementEditor.element.fillColor ? updatedElement.fillColor : null,
      color: updatedElement.textColor !== elementEditor.element.textColor ? updatedElement.textColor : null,
      stroke: updatedElement.borderColor !== elementEditor.element.borderColor ? updatedElement.borderColor : null,
      'stroke-dasharray': updatedElement.borderStyle !== elementEditor.element.borderStyle ? 
        (updatedElement.borderStyle === 'dashed' ? '5 5' : null) : null,
      'stroke-width': updatedElement.borderWidth !== elementEditor.element.borderWidth ?
        (updatedElement.borderWidth === 'thin' ? '1px' : 
         updatedElement.borderWidth === 'thick' ? '3px' : 
         updatedElement.borderWidth === 'none' ? '0px' : '2px') : null
    };
    
    // Check if any style property has changed
    const hasStyleChanges = Object.values(styleChanges).some(value => value !== null);
    
    if (hasStyleChanges) {
      // For nodes, update the style statement
      if (elementEditor.nodeType === 'node') {
        // Check if there's an existing style statement for this node
        const styleNodePattern = new RegExp(`style\\s+${cleanNodeId}\\s+([^\\n]+)`, 'g');
        const styleMatch = styleNodePattern.exec(updatedCode);
        
        if (styleMatch) {
          // Parse existing style properties
          const existingStyles = {};
          styleMatch[1].split(',').forEach(prop => {
            const [key, value] = prop.split(':').map(s => s.trim());
            if (key && value) {
              existingStyles[key] = value;
            }
          });
          
          // Update only the changed properties
          Object.entries(styleChanges).forEach(([key, value]) => {
            if (value !== null) {
              existingStyles[key] = value;
            }
          });
          
          // Convert back to style string
          const stylePropsArray = Object.entries(existingStyles).map(([key, value]) => `${key}:${value}`);
          
          // Replace the existing style statement
          updatedCode = updatedCode.replace(styleNodePattern, `style ${cleanNodeId} ${stylePropsArray.join(',')}`);
          console.log('Updated existing style statement');
        } else {
          // If no existing style statement, create a new one with only the changed properties
          const stylePropsArray = Object.entries(styleChanges)
            .filter(([key, value]) => value !== null)
            .map(([key, value]) => `${key}:${value}`);
          
          if (stylePropsArray.length > 0) {
            updatedCode += `\nstyle ${cleanNodeId} ${stylePropsArray.join(',')}`;
            console.log('Added new style statement');
          }
        }
      }
      // For edges, update the linkStyle statement
      else if (elementEditor.nodeType === 'edge') {
        // Find the edge index
        const edgePattern = /(\w+)\s*-->\s*(\w+)/g;
        const edges = [...updatedCode.matchAll(edgePattern)];
        let edgeIndex = -1;
        
        // For edges, we need to find the correct edge index
        for (let i = 0; i < edges.length; i++) {
          const edge = edges[i];
          const sourceNode = edge[1];
          const targetNode = edge[2];
          
          // Check if our node is part of this edge
          if (sourceNode === cleanNodeId || targetNode === cleanNodeId) {
            edgeIndex = i;
            console.log('Found edge at index:', edgeIndex);
            break;
          }
        }
        
        if (edgeIndex !== -1) {
          // Check if there's an existing linkStyle statement for this edge
          const linkStylePattern = new RegExp(`linkStyle\\s+${edgeIndex}\\s+([^\\n]+)`, 'g');
          const linkStyleMatch = linkStylePattern.exec(updatedCode);
          
          if (linkStyleMatch) {
            // Parse existing style properties
            const existingStyles = {};
            linkStyleMatch[1].split(',').forEach(prop => {
              const [key, value] = prop.split(':').map(s => s.trim());
              if (key && value) {
                existingStyles[key] = value;
              }
            });
            
            // Update only the changed properties
            // For edges, we map fillColor to stroke and textColor to color
            if (styleChanges.fill !== null) {
              existingStyles.stroke = styleChanges.fill;
            }
            if (styleChanges.color !== null) {
              existingStyles.color = styleChanges.color;
            }
            if (styleChanges.stroke !== null) {
              existingStyles.stroke = styleChanges.stroke;
            }
            if (styleChanges['stroke-dasharray'] !== null) {
              existingStyles['stroke-dasharray'] = styleChanges['stroke-dasharray'];
            }
            
            // Convert back to style string
            const stylePropsArray = Object.entries(existingStyles).map(([key, value]) => `${key}:${value}`);
            
            // Replace the existing linkStyle statement
            updatedCode = updatedCode.replace(linkStylePattern, `linkStyle ${edgeIndex} ${stylePropsArray.join(',')}`);
            console.log('Updated existing linkStyle statement');
          } else {
            // If no existing linkStyle statement, create a new one with only the changed properties
            const edgeStyleChanges = {
              stroke: styleChanges.fill !== null ? styleChanges.fill : 
                     (styleChanges.stroke !== null ? styleChanges.stroke : null),
              color: styleChanges.color !== null ? styleChanges.color : null,
              'stroke-dasharray': styleChanges['stroke-dasharray']
            };
            
            const stylePropsArray = Object.entries(edgeStyleChanges)
              .filter(([key, value]) => value !== null)
              .map(([key, value]) => `${key}:${value}`);
            
            if (stylePropsArray.length > 0) {
              updatedCode += `\nlinkStyle ${edgeIndex} ${stylePropsArray.join(',')}`;
              console.log('Added new linkStyle statement');
            }
          }
        } else {
          console.error(`Could not find edge index for node ${cleanNodeId}`);
        }
      }
    }
    
    return updatedCode;
  };
  
  // Direct click handler for SVG container
  const handleSvgContainerClick = (e) => {
    if (!isEditMode || !svg) return;
    
    // Check if the clicked element is a text element or node
    let target = e.target;
    let foundTextElement = null;
    let foundNodeElement = null;
    
    // First check if we clicked directly on a text element
    while (target && target !== svgContainerRef.current) {
      if (target.tagName === 'text') {
        foundTextElement = target;
        break;
      }
      
      // Check if this is a node element
      if (target.classList && 
          (target.classList.contains('node') || 
           target.classList.contains('cluster') || 
           target.classList.contains('edge'))) {
        foundNodeElement = target;
        // Don't break here, continue looking for text elements
      }
      
      // Check if this is a label that might contain text
      if (target.classList && target.classList.contains('label')) {
        // Try to find text elements inside this label
        const textElements = target.querySelectorAll('text');
        if (textElements.length > 0) {
          foundTextElement = textElements[0]; // Use the first text element
          break;
        } else if (target.textContent) {
          // If no text elements but has text content, create a mock text element
          foundTextElement = {
            textContent: target.textContent,
            getBoundingClientRect: () => target.getBoundingClientRect(),
            parentElement: target
          };
          break;
        }
      }
      
      target = target.parentElement;
    }
    
    // If we found a text element, handle it
    if (foundTextElement) {
      handleTextClick(e, foundTextElement);
    } 
    // Otherwise, if we found a node element, handle it
    else if (foundNodeElement) {
      handleNodeClick(e, foundNodeElement);
    }
    // If we clicked on the background, hide any open editors
    else {
      if (elementEditor.visible) {
        setElementEditor({ ...elementEditor, visible: false });
      }
    }
  };

  return (
    <div className="diagram-renderer">
      <div className="renderer-toolbar">
        <h2>Preview</h2>
        
        <div className="renderer-actions">
          <button 
            className="btn btn-secondary" 
            onClick={handleRender}
            disabled={loading}
            title="Render Diagram"
          >
            Render
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={handleZoomIn}
            disabled={!svg || loading || zoom >= 3}
            title="Zoom In"
          >
            <FiZoomIn size={16} />
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={handleZoomOut}
            disabled={!svg || loading || zoom <= 0.5}
            title="Zoom Out"
          >
            <FiZoomOut size={16} />
          </button>
          
          <button 
            className={`btn btn-secondary ${isPanning ? 'active' : ''}`} 
            onClick={handleTogglePanning}
            disabled={!svg || loading}
            title="Pan Mode"
          >
            <FiMove size={16} />
          </button>
          
          <button 
            className={`btn btn-secondary ${isEditMode ? 'active' : ''}`} 
            onClick={handleToggleEditMode}
            disabled={!svg || loading}
            title="Edit Mode"
          >
            <FiEdit2 size={16} />
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={handleResetView}
            disabled={!svg || loading}
            title="Reset View"
          >
            <FiMaximize size={16} />
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={exportSVG}
            disabled={!svg || loading}
            title="Export as SVG"
          >
            <FiDownload size={16} />
            <span className="ml-1">SVG</span>
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={exportPNG}
            disabled={!svg || loading}
            title="Export as PNG"
          >
            <FiDownload size={16} />
            <span className="ml-1">PNG</span>
          </button>
        </div>
      </div>
      
      <div 
        className={`renderer-content ${isPanning ? 'panning-mode' : ''} ${isEditMode ? 'edit-mode' : ''} ${elementEditor.visible ? 'renderer-content-with-sidebar' : ''}`} 
        ref={rendererRef}
        onMouseDown={handleMouseDown}
      >
        {loading ? (
          <div className="spinner-container">
            <div className="spinner"></div>
            <p>Rendering diagram...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">Error rendering diagram:</p>
            <pre>{error}</pre>
          </div>
        ) : svg ? (
          <div 
            ref={svgContainerRef}
            className={`svg-container`}
            style={{
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
              cursor: isPanning ? 'grab' : isEditMode ? 'pointer' : 'default'
            }}
            onClick={handleSvgContainerClick}
            dangerouslySetInnerHTML={{ __html: svg }} 
          />
        ) : (
          <div className="empty-state">
            <p>Enter Mermaid code in the editor to see the preview</p>
          </div>
        )}
        
        {isEditMode && (
          <div className="edit-mode-indicator">
            <p>Click on elements to edit them</p>
          </div>
        )}
        
        {/* Element editor sidebar - moved back inside renderer-content */}
        <ElementEditor
          visible={elementEditor.visible}
          element={elementEditor.element}
          nodeId={elementEditor.nodeId}
          onClose={() => setElementEditor({ ...elementEditor, visible: false })}
          onSave={handleElementEditorSave}
          onDelete={handleElementEditorDelete}
          onCreateEdge={handleElementEditorCreateEdge}
        />
      </div>
    </div>
  );
};

export default DiagramRenderer; 